import {
    ICameraVideoTrack,
    IMicrophoneAudioTrack
} from "agora-rtc-sdk-ng";
import {getDevices} from "../helpers/DevicesHelper";
import {createAudioTrack, createVideoTrack, releaseTrack} from "../helpers/TracksHelper";
import {AgoraQualityCheckerInterface} from "../interfaces/AgoraQualityCheckerInterface";
import {WebConferenceRepository} from "@api/repositories/WebConferenceRepository";
import {AgoraTestCredentials} from "@api/models/interfaces/web-conference/AgoraTestCredentials";
import {Store} from "vuex";
import {AgoraTestClient} from "@share/web-conferences/agora/AgoraTestClient";

export class AgoraQualityChecker implements AgoraQualityCheckerInterface {
    private testCredentials: AgoraTestCredentials = {} as any;

    uplinkClient: AgoraTestClient | null = null;
    downlinkClient: AgoraTestClient | null =  null;

    private audioTrack: IMicrophoneAudioTrack | null = null;
    private videoTrack: ICameraVideoTrack | null = null;

    constructor(
        private readonly repository: WebConferenceRepository,
        private readonly store: Store<any>
    ) {
        this.uplinkClient = new AgoraTestClient('uplink');
        this.downlinkClient = new AgoraTestClient('downlink');
    }

    async test(): Promise<boolean> {
        await this.getCredentials();

        if (!this.isCredentialsSet()) {
            return false;
        }

        await this.joinClients();

        let videoDevices: MediaDeviceInfo[], audioDevices: MediaDeviceInfo[];

        [videoDevices, audioDevices] = await getDevices();

        if (audioDevices.length == 0 || videoDevices.length == 0) {
            return false;
        }

        await this.createTracks(audioDevices[0].deviceId, videoDevices[0].deviceId);

        if (this.audioTrack == null || this.videoTrack == null) {
            await this.releaseTracks();
            return false;
        }

        this.downlinkClient?.attachUserPublishedEvent();

        if (!await this.publishTracks()) {
            return false;
        }

        this.initQualityNetworkListeners();

        return true;
    }

    private initQualityNetworkListeners() {
        this.uplinkClient?.initQualityNetworkListener();
        this.downlinkClient?.initQualityNetworkListener();
    }

    private async createTracks(audioDeviceId: string, videoDeviceId: string) {
        this.audioTrack = await createAudioTrack({}, audioDeviceId);
        this.videoTrack = await createVideoTrack({}, videoDeviceId);
    }

    private async publishTracks() {
        try {
            await this.uplinkClient?.client?.publish([
                this.audioTrack as IMicrophoneAudioTrack,
                this.videoTrack as ICameraVideoTrack
            ]);
            return true;
        }
        catch (e) {
            this.store.dispatch('reportError', e);
            return false;
        }
    }

    private async joinClients() {
        const {channelId, appId} = this.testCredentials;
        try {
            await this.uplinkClient?.join(appId, channelId, this.testCredentials.upLinkToken, this.testCredentials.upLinkUserId);
            await this.downlinkClient?.join(appId, channelId, this.testCredentials.downLinkToken, this.testCredentials.downLinkUserId);
            return true;
        }
        catch (e) {
            this.store.dispatch('reportError', e);
            return false;
        }
    }

    async getCredentials() {
        try {
            this.testCredentials = await this.repository.getTestCredentials();
        }
        catch (e) {
            this.testCredentials = {} as any;
            this.store.dispatch('reportError', e);
        }
    }

    private isCredentialsSet() {
        return typeof this.testCredentials == 'object' && this.testCredentials.appId;
    }

    private async releaseTracks() {
        await releaseTrack(this.audioTrack);
        await releaseTrack(this.videoTrack);
        this.audioTrack = null;
        this.videoTrack = null;
    }

    async clean() {
        await this.uplinkClient?.clean();
        await this.downlinkClient?.clean();

        await this.releaseTracks();
    }
}
